/**
 * Turns a row read back by `QUERY_CARRIER_ASSIGNMENT_BY_ID` into a patch for the
 * edit modal's order clone.
 *
 * The clone only resyncs when the `order` prop's identity changes, and every
 * caller holds that order in its own `useState`, so nothing re-reads it after an
 * out-of-band write like `carrier/unassign`. Left stale the modal keeps showing
 * the old carrier — and, because `carrier_id` / `middle_mile_carrier_id` are not
 * read-only and are in `UPSERT_ORDERS`'s `update_columns`, a subsequent Done
 * writes the unassigned carrier straight back onto the order.
 *
 * Allowlisted rather than spread wholesale: the query and the clone are the same
 * shape, so a field added to the fragment would otherwise start overwriting the
 * clone silently. This is a patch over an in-progress edit, not a replacement of
 * it — anything outside this list belongs to the user's unsaved work.
 *
 * Scoped to one leg, because the endpoint tears down one leg and leaves the other
 * running. Patching both did two things wrong to the leg nobody touched: it
 * replaced its rendered carrier with this read's two-field shape, narrower than
 * the `ClientFields + user + locations` the order detail page hands the modal; and
 * it reverted an unsaved assignment on that leg to the server's value. The second
 * is the sharper one -- assigning a final mile carrier also sets `order_status`,
 * which is not on any list here, so a middle mile unassign used to leave the clone
 * claimed against the *old* carrier_id for Done to save.
 */

/**
 * The legs `carrier/unassign` accepts, and the key this module scopes on. Exported
 * so the caller posting the leg and the map keyed on it cannot drift -- a string
 * that misses falls back to the unscoped patch, which is the bug this split fixes.
 */
export const UNASSIGN_LEGS = {
    DISPATCH: 'DISPATCH',
    MIDDLE_MILE: 'MIDDLE_MILE',
};

/**
 * Final mile columns patched after a dispatch unassign. `carrier_assignment_source`
 * rides with `carrier_id` because `modifyOrder` writes the pair together.
 * `order_carrier` is the relationship the tables and cards display, so clearing the
 * id alone would leave them on the old carrier.
 */
export const DISPATCH_ASSIGNMENT_FIELDS = [
    'carrier_id',
    'carrier_assignment_source',
    'dispatch_integration_type',
    'dispatch_integration_meta',
    'frayt_id',
    'order_carrier',
];

/**
 * Line haul columns patched after a middle mile unassign. Carries the same
 * relationship/id split as `DISPATCH_ASSIGNMENT_FIELDS`.
 */
export const MIDDLE_MILE_ASSIGNMENT_FIELDS = [
    'middle_mile_carrier_id',
    'middle_mile_integration_type',
    'middle_mile_integration_status',
    'middle_mile_integration_meta',
    'order_middle_mile_carrier',
];

/**
 * Order-level columns refreshed on either leg's unassign. `routes` spans the whole
 * order, and `external_integration_attr` follows whichever integration the order has.
 * Neither is editable in the modal, so refreshing them cannot clobber unsaved work.
 */
export const SHARED_ASSIGNMENT_FIELDS = ['routes', 'external_integration_attr'];

/**
 * Every column this module can patch. Still the pairing the fragment is checked
 * against, so a column added to one of the lists above is a column the read has to
 * select -- `orders.carrierAssignment.test.js` guards that.
 */
export const CARRIER_ASSIGNMENT_FIELDS = [
    ...DISPATCH_ASSIGNMENT_FIELDS,
    ...MIDDLE_MILE_ASSIGNMENT_FIELDS,
    ...SHARED_ASSIGNMENT_FIELDS,
];

const FIELDS_BY_LEG = {
    [UNASSIGN_LEGS.DISPATCH]: [...DISPATCH_ASSIGNMENT_FIELDS, ...SHARED_ASSIGNMENT_FIELDS],
    [UNASSIGN_LEGS.MIDDLE_MILE]: [...MIDDLE_MILE_ASSIGNMENT_FIELDS, ...SHARED_ASSIGNMENT_FIELDS],
};

/**
 * @param {Object|null|undefined} row - `data.order` from the assignment read.
 * @param {string} [leg] - the leg that was unassigned, one of `UNASSIGN_LEGS`. An
 *     unrecognized or missing leg patches every column: a carrier the endpoint
 *     just cleared, left sitting in the clone for Done to write back, is a worse
 *     outcome than a narrowed relationship on the other leg.
 * @returns {Object|null} the clone patch, or null when there is nothing to apply.
 */
export const assignmentPatch = (row, leg) => {
    if (!row) return null;

    const fields = FIELDS_BY_LEG[leg] || CARRIER_ASSIGNMENT_FIELDS;

    // `undefined` and a genuine null are different answers here: null is the
    // cleared value we are specifically trying to propagate, while undefined
    // means the field was not in the response and must not overwrite anything.
    const patch = Object.fromEntries(
        fields.filter((field) => row[field] !== undefined).map((field) => [field, row[field]])
    );

    return Object.keys(patch).length ? patch : null;
};
